/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  HStack, DetailStack, CircularGaugeChart, ActionIconDropdown, Dropdown,
} from '@doveit/bricks';
import { pluralizeWord, formatEuro } from '@doveit/hammer';
import useIndividualGoal from '../../hooks/use-individual-goal/useIndividualGoal';
import { JobTitle, MembershipStatus } from '../../../domain/types';
import useIndividualRank from '../../hooks/use-individual-rank/useIndividualRank';
import useManagerGoal from '../../hooks/use-manager-goal/useManagerGoal';
import { calculateMembershipStatus } from '../../utils/calculateMembershipStatus';
import { Agent } from '../../../providers/api/dtos';
import { jobTitleLabels } from '../../../labels';
import { setVerticalEllipsis } from '../../../utils/styles/verticalEllipsis.style';
import { ViewAgentSmallPreview } from '../../components';
import AgencyPopover from '../../components/agency-popover/AgencyPopover';
import AgentRankPosition from '../../components/agent-rank-position/AgentRankPosition';
import GoalPopover from '../../components/goal-popover/GoalPopover';
import ProvincePopover from '../../components/province-popover/ProvincePopover';
import { configByPerformanceRating } from '../../constants';
import { toAgentPreviewProps } from '../../mappers/toAgentPreviewProps';
import MigrateAgentDataAction from '../migrate-agent-data-action/MigrateAgentDataAction';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';

const managerAndExecutive = [JobTitle.AREA_MANAGER, JobTitle.EXECUTIVE];

export interface ViewAgentHeaderProps {
  agent: Agent
  onSuccessMigrate?: (agent: Agent) => void
}

const ViewAgentHeader: React.FC<ViewAgentHeaderProps> = ({
  agent,
  onSuccessMigrate,
}) => {
  const isMobile = useIsDevice('mobile');
  const { data: individualRank } = useIndividualRank(agent && !managerAndExecutive.includes(agent?.jobTitle) ? agent?.id : undefined);

  const { data: individualGoal } = useIndividualGoal(agent && ![...managerAndExecutive, JobTitle.BROKER].includes(agent?.jobTitle) ? agent?.id : undefined);
  const { data: managerGoal } = useManagerGoal(agent && managerAndExecutive.includes(agent?.jobTitle) ? agent?.id : undefined);

  const rank = React.useMemo(() => individualRank?.find(({ rankType }) => rankType === 'AGENT')?.rank, [individualRank]);
  const goal = React.useMemo(() => {
    if (individualGoal) {
      return ({
        progress: individualGoal.metrics.sales.yearly.progress,
        status: individualGoal.metrics.sales.yearly.status,
        target: individualGoal.target,
      });
    }

    if (managerGoal) {
      return ({
        progress: managerGoal.metrics.sales.yearly.progress,
        status: managerGoal.metrics.sales.yearly.status,
        target: managerGoal.target.manager,
      });
    }

    return undefined;
  }, [individualGoal, managerGoal]);

  const membershipStatus = React.useMemo(() => {
    if (agent) {
      return calculateMembershipStatus(
        new Date(agent.contract.startDate),
        agent.contract.endDate ? new Date(agent.contract.endDate) : undefined,
      );
    }

    return undefined;
  }, [agent]);

  if (!agent) {
    return null;
  }

  return (
    <HStack
      aria-label="Header informazioni agente"
      justifyContent="space-between"
    >
      <HStack gap={150}>
        <ViewAgentSmallPreview
          {...toAgentPreviewProps(agent)}
          details={`${jobTitleLabels[agent.jobTitle]}${agent.isRookie ? ' (Rookie)' : ''}`}
        />

        {!isMobile && (
          <>
            {(agent.coveredGeo?.length || 0) > 0 && (
              <ProvincePopover
                coveredGeo={agent.coveredGeo}
                trigger={(
                  <DetailStack
                    label={`${agent.coveredGeo!.length} ${pluralizeWord('provinc', 'ia', 'e', agent.coveredGeo!.length)} ${pluralizeWord('copert', 'a', 'e', agent.coveredGeo!.length)}`}
                  >
                    <div css={setVerticalEllipsis(1)}>
                      {agent.coveredGeo!.map(({ province, weight }) => `${province} (${weight * 100}%)`).join(', ')}
                    </div>
                  </DetailStack>
                )}
              />
            )}
            {((agent.agencies?.length || 0) > 0 && (
              <AgencyPopover
                agencies={agent.agencies}
                trigger={(
                  <DetailStack
                    label={`${agent.agencies!.length} ${pluralizeWord('agenzi', 'a', 'e', agent.agencies!.length)}`}
                  >
                    <div css={setVerticalEllipsis(1)}>
                      {agent.agencies!.map(({ shortName, name }) => shortName || name).join(', ')}
                    </div>
                  </DetailStack>
                )}
              />
            ))}
          </>
        )}
      </HStack>

      <HStack gap={150}>
        {(rank || goal) && (
          <>
            {rank && (
              <AgentRankPosition
                rank={rank}
                size="S"
              />
            )}
            {goal && (
              <HStack gap={100} wrap="nowrap">
                <>
                  {!isMobile && (
                    <CircularGaugeChart
                      progress={goal.progress}
                      size="S"
                      color={configByPerformanceRating[goal.status].color}
                      hideValue
                    />
                  )}

                  {isMobile && (
                    <GoalPopover
                      trigger={(
                        <CircularGaugeChart
                          progress={goal.progress}
                          size="S"
                          color={configByPerformanceRating[goal.status].color}
                          hideValue
                        />
                      )}
                      goal={goal}
                    />
                  )}
                </>

                {!isMobile && goal.target && (
                  <DetailStack
                    label="Obiettivo"
                    aria-label="Obiettivo"
                  >
                    {formatEuro(goal.target)}
                  </DetailStack>
                )}
              </HStack>
            )}
          </>
        )}

        {membershipStatus === MembershipStatus.REVOKED && (
          !isMobile ? (
            <MigrateAgentDataAction
              agent={agent}
              onSuccess={onSuccessMigrate}
            />
          ) : (
            <ActionIconDropdown
              label="Azioni rapide sull'agente"
              aria-label="Azioni rapide sull'agente"
              emphasis="low"
            >
              <MigrateAgentDataAction
                agent={agent}
                onSuccess={onSuccessMigrate}
              >
                {({ onClick, isLoading }) => (
                  <Dropdown.Option
                    aria-label="Trasferisci i dati dell'agente"
                    title="Trasferisci i dati dell'agente"
                    label="Trasferisci"
                    onClick={onClick}
                    loading={isLoading}
                  />
                )}
              </MigrateAgentDataAction>
            </ActionIconDropdown>
          )
        )}
      </HStack>
    </HStack>
  );
};

export default ViewAgentHeader;
